
import React, { useState, useRef } from 'react';
import {
    CheckCircle2,
    Upload,
    User,
    Mail,
    Briefcase,
    BookOpen,
    Mic,
    Square,
    Loader2,
    ArrowRight,
    ShieldCheck,
    Award,
    FileText
} from 'lucide-react';
import { createAudioRecordingHandler } from './AudioService';
import { API_URL } from './constants';
import { handleResponseError } from '../utils/errorHandler';

const ReviewerApplication = ({ user }) => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: user?.email || '',
        linkedinProfile: '',
        domains: [],
        bio: '',
        experienceYears: '',
        motivation: ''
    });

    const [domains] = useState([
        'Healthcare', 'Legal', 'Finance', 'Technology', 'Science',
        'Creative Writing', 'Entertainment', 'Education', 'Other'
    ]);

    const [documents, setDocuments] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Voice Recording State
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    // Notification state (mock, can be replaced with actual toast)
    const [notification, setNotification] = useState(null);

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 4000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDomainToggle = (domain) => {
        setFormData(prev => {
            const currentDomains = prev.domains;
            if (currentDomains.includes(domain)) {
                return { ...prev, domains: currentDomains.filter(d => d !== domain) };
            } else {
                return { ...prev, domains: [...currentDomains, domain] };
            }
        });
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            // if (file.size > 50 * 1024) {
            //     showNotification('error', 'File size exceeds 50KB limit.');
            //     return;
            // }
            setDocuments(file);
        }
    };

    // Audio Handler
    const audioHandler = createAudioRecordingHandler({
        mediaRecorderRef,
        audioChunksRef,
        isRecording,
        setIsRecording,
        setIsTranscribing,
        user: user || { uid: 'anonymous' },
        onTranscriptionComplete: (text) => {
            setFormData(prev => ({
                ...prev,
                motivation: prev.motivation ? prev.motivation + ' ' + text : text
            }));
        },
        showNotification
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Create FormData to handle file upload
            const formDataToSend = new FormData();

            // Append all form fields
            formDataToSend.append('user_id', user?.uid || '');
            formDataToSend.append('full_name', formData.fullName);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('linkedin_profile', formData.linkedinProfile);
            formDataToSend.append('domains', JSON.stringify(formData.domains));
            formDataToSend.append('experience_years', formData.experienceYears);
            formDataToSend.append('bio', formData.motivation);

            // Append file if it exists
            if (documents) {
                formDataToSend.append('qualification_document', documents);
            }

            // Use fetch with error handling
            const response = await fetch(`${API_URL}/api/reviewer-application`, {
                method: 'POST',
                headers: {
                    //'Authorization': `Bearer ${user?.accessToken || ''}`,
                },
                body: formDataToSend
            });

            // Handle 401 errors automatically
            await handleResponseError(response);

            const result = await response.json();

            if (response.ok) {
                setIsSubmitting(false);
                setSubmitted(true);
                window.scrollTo(0, 0);
            } else {
                throw new Error(result.message || 'Failed to submit application');
            }
        } catch (error) {
            console.error('Error submitting application:', error);
            setIsSubmitting(false);
            showNotification('error', error.message || 'Failed to submit application. Please try again.');
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 font-inter">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
                    <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 size={40} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Received!</h2>
                    <p className="text-gray-600 mb-8">
                        Thank you for applying to be a domain expert reviewer. Our team will review your qualifications and get back to you if your profile matches our current needs.
                    </p>
                    <div className="p-4 bg-blue-50 text-blue-800 rounded-lg text-sm mb-6">
                        Please keep an eye on your email for next steps regarding the vetting process.
                    </div>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors w-full"
                    >
                        Return Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 font-inter">
            {/* Header / Hero Section */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-5xl mx-auto px-6 py-16 text-center">
                    <h1 className="text-4xl font-extrabold text-blue-700 mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-blue-500">
                        Join Our Expert Reviewer Network
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
                        Help us ensure the highest quality of synthetic data. Verify, correct, and certify datasets in your domain of expertise.
                    </p>

                    {/* Process Steps */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left mt-12 bg-gray-50 p-8 rounded-2xl border border-gray-100">
                        <div className="relative pl-4">
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-200 rounded-full"></div>
                            <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">1. Apply & Verify</h3>
                            <p className="text-sm text-gray-600">Submit your profile and credentials. We vet all reviewers to ensure domain authority.</p>
                        </div>
                        <div className="relative pl-4">
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-200 rounded-full"></div>
                            <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">2. Get Matched</h3>
                            <p className="text-sm text-gray-600">When data needs review in your field (e.g., Medical, Legal), we reach out to you directly.</p>
                        </div>
                        <div className="relative pl-4">
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-200 rounded-full"></div>
                            <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">3. Review & Earn</h3>
                            <p className="text-sm text-gray-600">Review the data, provide corrections, and get compensated for your expertise.</p>
                        </div>
                    </div>
                </div>
            </div>

            {notification && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${notification.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                    {notification.message}
                </div>
            )}

            {/* Application Form */}
            <div className="max-w-3xl mx-auto px-6 py-12">
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-8">

                    <div className="space-y-6">
                        <h2 className="text-xl font-bold text-gray-900 border-b pb-4 flex items-center gap-2">
                            <User size={20} className="text-blue-600" />
                            Personal Details
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Full Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="fullName"
                                    required
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                    placeholder="John Doe"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Email Address <span className="text-red-500">*</span></label>
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                    placeholder="john@example.com"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">LinkedIn Profile / Portfolio URL</label>
                            <input
                                type="url"
                                name="linkedinProfile"
                                value={formData.linkedinProfile}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                placeholder="https://linkedin.com/in/johndoe"
                            />
                        </div>
                    </div>

                    <div className="space-y-6">
                        <h2 className="text-xl font-bold text-gray-900 border-b pb-4 flex items-center gap-2">
                            <ShieldCheck size={20} className="text-blue-600" />
                            Expertise & Domain
                        </h2>

                        <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-700">Select Your Domains of Expertise <span className="text-red-500">*</span></label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {domains.map(domain => (
                                    <button
                                        key={domain}
                                        type="button"
                                        onClick={() => handleDomainToggle(domain)}
                                        className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all text-left flex items-center justify-between ${formData.domains.includes(domain)
                                            ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                            : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {domain}
                                        {formData.domains.includes(domain) && <CheckCircle2 size={16} />}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Select all that apply. Note: Healthcare and Legal domains require verification documents.</p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Years of Experience</label>
                            <select
                                name="experienceYears"
                                value={formData.experienceYears}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all bg-white"
                            >
                                <option value="">Select experience level</option>
                                <option value="0-2">0-2 years</option>
                                <option value="3-5">3-5 years</option>
                                <option value="5-10">5-10 years</option>
                                <option value="10+">10+ years</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Proof of Qualifications (PDF/Doc)</label>
                            <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${documents ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                                }`}>
                                <input
                                    type="file"
                                    id="file-upload"
                                    className="hidden"
                                    onChange={handleFileChange}
                                    accept=".pdf,.doc,.docx"
                                />
                                <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-2">
                                    {documents ? (
                                        <>
                                            <FileText size={32} className="text-green-600" />
                                            <span className="font-medium text-green-700">{documents.name}</span>
                                            <span className="text-xs text-green-600">Click to change file</span>
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={32} className="text-gray-400" />
                                            <span className="font-medium text-gray-600">Click to upload qualifications</span>
                                            <span className="text-xs text-gray-400">Required for specialized domains (Medical, Legal)</span>
                                        </>
                                    )}
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <h2 className="text-xl font-bold text-gray-900 border-b pb-4 flex items-center gap-2">
                            <FileText size={20} className="text-blue-600" />
                            About You
                        </h2>

                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <label className="text-sm font-medium text-gray-700">Brief Bio / Motivation</label>
                                <button
                                    type="button"
                                    onClick={audioHandler.toggleRecording}
                                    disabled={isTranscribing}
                                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${isRecording
                                        ? 'bg-red-100 text-red-600 animate-pulse'
                                        : isTranscribing
                                            ? 'bg-blue-100 text-blue-600'
                                            : 'bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                                        }`}
                                >
                                    {isRecording ? (
                                        <><Square size={12} /> Stop Recording</>
                                    ) : isTranscribing ? (
                                        <><Loader2 size={12} className="animate-spin" /> Transcribing...</>
                                    ) : (
                                        <><Mic size={12} /> Voice Input</>
                                    )}
                                </button>
                            </div>
                            <textarea
                                name="motivation"
                                required
                                value={formData.motivation}
                                onChange={handleInputChange}
                                rows={5}
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none"
                                placeholder="Tell us about your background and why you want to review for us..."
                            />
                            <p className="text-xs text-gray-500">Feel free to type or use the voice input button to speak your bio.</p>
                        </div>
                    </div>

                    <div className="pt-6">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 hover:scale-[1.01] transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? <Loader2 size={24} className="animate-spin" /> : <>Submit Application <ArrowRight size={20} /></>}
                        </button>
                        <p className="text-center text-xs text-gray-500 mt-4">
                            By submitting, you agree to our Terms of Service and vetting process.
                        </p>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default ReviewerApplication;
